import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IoClose, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import './ChatImageLightbox.css';

type ChatImageLightboxProps = {
  urls: string[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
};

export function ChatImageLightbox({
  urls,
  index,
  onClose,
  onIndexChange,
}: ChatImageLightboxProps) {
  const hasMany = urls.length > 1;
  const current = urls[index] ?? urls[0];

  const goPrev = useCallback(() => {
    if (!hasMany) return;
    onIndexChange((index - 1 + urls.length) % urls.length);
  }, [hasMany, index, onIndexChange, urls.length]);

  const goNext = useCallback(() => {
    if (!hasMany) return;
    onIndexChange((index + 1) % urls.length);
  }, [hasMany, index, onIndexChange, urls.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [goNext, goPrev, onClose]);

  if (!current) return null;

  return createPortal(
    <div
      className="chat-image-lightbox"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <button
        type="button"
        className="chat-image-lightbox__close"
        aria-label="Закрыть"
        onClick={onClose}
      >
        <IoClose size={28} />
      </button>
      {hasMany ? (
        <button
          type="button"
          className="chat-image-lightbox__nav chat-image-lightbox__nav--prev"
          aria-label="Предыдущее"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
        >
          <IoChevronBack size={28} />
        </button>
      ) : null}
      <img
        src={current}
        alt=""
        className="chat-image-lightbox__img"
        onClick={(e) => e.stopPropagation()}
      />
      {hasMany ? (
        <button
          type="button"
          className="chat-image-lightbox__nav chat-image-lightbox__nav--next"
          aria-label="Следующее"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
        >
          <IoChevronForward size={28} />
        </button>
      ) : null}
      {hasMany ? (
        <div className="chat-image-lightbox__counter" onClick={(e) => e.stopPropagation()}>
          {index + 1} / {urls.length}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
